import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar } from '../components/ui/calendar';
import {
  CalendarIcon, CheckCircle2, XCircle, Clock, AlertTriangle,
  TrendingUp, TrendingDown, Minus, Filter, ChevronDown, ChevronUp
} from 'lucide-react';

const PRESETS = [
  { key: 'today', label: 'Today' },
  { key: 'yesterday', label: 'Yesterday' },
  { key: 'last7', label: 'Last 7 Days' },
  { key: 'mtd', label: 'MTD' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'custom', label: 'Custom' },
];

function getPresetDates(key) {
  const today = new Date();
  switch (key) {
    case 'today': return { start: today, end: today };
    case 'yesterday': { const y = subDays(today, 1); return { start: y, end: y }; }
    case 'last7': return { start: subDays(today, 6), end: today };
    case 'mtd': return { start: startOfMonth(today), end: today };
    case 'lastMonth': { const lm = subMonths(today, 1); return { start: startOfMonth(lm), end: endOfMonth(lm) }; }
    default: return { start: today, end: today };
  }
}

function ScoreDisplay({ score, size = 'lg' }) {
  const color = score >= 80 ? '#2D6A4F' : score >= 50 ? '#D4A017' : '#C45A41';
  const cls = size === 'lg' ? 'text-2xl' : size === 'md' ? 'text-lg' : 'text-sm';
  return <span className={`${cls} font-bold`} style={{ color }}>{score}%</span>;
}

function TrendBadge({ trend, prevAvg }) {
  if (trend === 0 && prevAvg === 0) return <span className="text-xs text-[#5C6B64]">No prior data</span>;
  const isUp = trend > 0;
  const isDown = trend < 0;
  const Icon = isUp ? TrendingUp : isDown ? TrendingDown : Minus;
  const color = isUp ? '#2D6A4F' : isDown ? '#C45A41' : '#5C6B64';
  const bg = isUp ? '#2D6A4F' : isDown ? '#C45A41' : '#5C6B64';
  return (
    <div className="flex items-center gap-1" data-testid="trend-badge">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-xs font-semibold" style={{ color }}>
        {isUp ? '+' : ''}{trend}%
      </span>
      <span className="text-[10px] text-[#5C6B64]">vs prev</span>
    </div>
  );
}

function MiniBar({ data }) {
  if (!data || data.length === 0) return null;
  const max = Math.max(...data.map(d => d.adherence), 1);
  return (
    <div className="flex items-end gap-[2px] h-8" data-testid="mini-trend-chart">
      {data.map((d, i) => (
        <div key={`${d.date || i}-${d.adherence}`} className="flex-1 min-w-[3px] max-w-[6px] rounded-t-sm transition-all"
          style={{
            height: `${Math.max((d.adherence / max) * 100, 4)}%`,
            backgroundColor: d.adherence >= 80 ? '#2D6A4F' : d.adherence >= 50 ? '#D4A017' : '#C45A41',
            opacity: d.is_on_time ? 1 : 0.5
          }}
          title={`${d.date}: ${d.adherence}%`} />
      ))}
    </div>
  );
}

export default function AdminChecklistDashboard() {
  const { getHeaders, API } = useAuth();
  const [preset, setPreset] = useState('today');
  const [startDate, setStartDate] = useState(new Date());
  const [endDate, setEndDate] = useState(new Date());
  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);
  const [singleData, setSingleData] = useState(null);
  const [rangeData, setRangeData] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOutlet, setExpandedOutlet] = useState(null);
  const [drilldownData, setDrilldownData] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  const isSingleDay = preset === 'today' || preset === 'yesterday';

  const applyPreset = useCallback((key) => {
    setPreset(key);
    if (key !== 'custom') {
      const { start, end } = getPresetDates(key);
      setStartDate(start);
      setEndDate(end);
    }
  }, []);

  useEffect(() => { applyPreset('today'); }, [applyPreset]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const sd = format(startDate, 'yyyy-MM-dd');
      const ed = format(endDate, 'yyyy-MM-dd');
      const issueParams = sd === ed ? `date=${sd}` : `start_date=${sd}&end_date=${ed}`;
      const [issuesRes] = await Promise.all([
        axios.get(`${API}/admin/checklist/issues?${issueParams}`, { headers: getHeaders() })
      ]);
      setIssues(issuesRes.data.issues || []);
      if (sd === ed) {
        const res = await axios.get(`${API}/admin/checklist/dashboard?date=${sd}`, { headers: getHeaders() });
        setSingleData(res.data);
        setRangeData(null);
      } else {
        const res = await axios.get(`${API}/admin/checklist/dashboard/range?start_date=${sd}&end_date=${ed}`, { headers: getHeaders() });
        setRangeData(res.data);
        setSingleData(null);
      }
    } catch { toast.error('Failed to load dashboard'); }
    finally { setLoading(false); }
  }, [API, getHeaders, startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  const toggleDrilldown = async (outletId) => {
    if (expandedOutlet === outletId) {
      setExpandedOutlet(null);
      setDrilldownData(null);
      return;
    }
    setExpandedOutlet(outletId);
    setDrilldownLoading(true);
    try {
      const sd = format(startDate, 'yyyy-MM-dd');
      const res = await axios.get(`${API}/admin/checklist/detail/${outletId}?date=${sd}`, { headers: getHeaders() });
      setDrilldownData(res.data);
    } catch { toast.error('Failed to load details'); }
    finally { setDrilldownLoading(false); }
  };

  return (
    <div data-testid="admin-checklist-dashboard">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-lg sm:text-xl tracking-tight font-semibold text-[#1A2421]">Checklist Compliance</h2>
          <p className="text-sm text-[#5C6B64] mt-1">
            {isSingleDay
              ? format(startDate, 'dd MMM yyyy')
              : `${format(startDate, 'dd MMM')} - ${format(endDate, 'dd MMM yyyy')}`}
          </p>
        </div>
      </div>

      {/* Date range filters */}
      <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="date-filter-bar">
        {PRESETS.map(p => (
          <Button key={p.key} variant={preset === p.key ? 'default' : 'outline'} size="sm"
            onClick={() => applyPreset(p.key)}
            className={preset === p.key
              ? 'bg-[color:var(--color-primary)] text-white hover:bg-[color:var(--color-primary-hover)]'
              : 'border-[#E2E8DF] text-[#5C6B64] hover:bg-[#F3F5F2]'}
            data-testid={`filter-${p.key}`}>
            {p.key === 'custom' && <Filter className="w-3 h-3 mr-1" />}
            {p.label}
          </Button>
        ))}
        {preset === 'custom' && (
          <div className="flex items-center gap-2 ml-1">
            <Popover open={startOpen} onOpenChange={setStartOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="border-[#E2E8DF] text-[#1A2421]" data-testid="custom-start-picker">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[#5C6B64]" />
                  {format(startDate, 'dd MMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={startDate} onSelect={d => { if (d) { setStartDate(d); setStartOpen(false); } }} disabled={d => d > new Date()} />
              </PopoverContent>
            </Popover>
            <span className="text-[#5C6B64] text-sm">to</span>
            <Popover open={endOpen} onOpenChange={setEndOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="border-[#E2E8DF] text-[#1A2421]" data-testid="custom-end-picker">
                  <CalendarIcon className="w-3.5 h-3.5 mr-1.5 text-[#5C6B64]" />
                  {format(endDate, 'dd MMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={endDate} onSelect={d => { if (d) { setEndDate(d); setEndOpen(false); } }} disabled={d => d > new Date()} />
              </PopoverContent>
            </Popover>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20"><div className="animate-spin w-8 h-8 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" /></div>
      ) : singleData ? (
        /* ===== SINGLE DAY VIEW ===== */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {singleData.outlets?.map(outlet => (
            <Card key={outlet.outlet_id} className={`border-[#E2E8DF] shadow-sm ${isSingleDay && outlet.status !== 'no_items' ? 'cursor-pointer hover:border-[color:var(--color-primary)]/30 transition-colors' : ''}`}
              data-testid={`compliance-card-${outlet.outlet_id}`}
              onClick={() => isSingleDay && outlet.status !== 'no_items' && toggleDrilldown(outlet.outlet_id)}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base text-[#1A2421]">{outlet.outlet_name}</CardTitle>
                    {isSingleDay && outlet.status !== 'no_items' && (
                      expandedOutlet === outlet.outlet_id
                        ? <ChevronUp className="w-4 h-4 text-[#5C6B64]" />
                        : <ChevronDown className="w-4 h-4 text-[#5C6B64]" />
                    )}
                  </div>
                  {outlet.status === 'completed' ? (
                    <Badge className={outlet.is_on_time
                      ? 'bg-[#2D6A4F]/10 text-[#2D6A4F] border-[#2D6A4F]/20'
                      : 'bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20'}
                      data-testid={`outlet-timing-${outlet.outlet_id}`}>
                      {outlet.is_on_time ? <><CheckCircle2 className="w-3 h-3 mr-1" />On Time</> : <><Clock className="w-3 h-3 mr-1" />Late</>}
                    </Badge>
                  ) : outlet.status === 'partial' ? (
                    <Badge className="bg-[#D4A017]/10 text-[#D4A017] border-[#D4A017]/20" data-testid={`outlet-status-${outlet.outlet_id}`}>
                      <Clock className="w-3 h-3 mr-1" />Partial
                    </Badge>
                  ) : outlet.status === 'pending' ? (
                    <Badge className="bg-[#C45A41]/10 text-[#C45A41] border-[#C45A41]/20" data-testid={`outlet-status-${outlet.outlet_id}`}>
                      <AlertTriangle className="w-3 h-3 mr-1" />Pending
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64]">No Items</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {outlet.status === 'no_items' ? (
                  <p className="text-sm text-[#5C6B64]">No checklist items assigned</p>
                ) : outlet.status === 'pending' ? (
                  <div className="text-center py-4">
                    <XCircle className="w-8 h-8 text-[#C45A41] mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#C45A41]">Checklist Not Submitted</p>
                    <p className="text-xs text-[#5C6B64] mt-1">{outlet.total_assigned} items pending</p>
                  </div>
                ) : outlet.status === 'partial' ? (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-[#5C6B64]">Overall Adherence</p>
                        <ScoreDisplay score={outlet.overall_adherence} />
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#5C6B64]">Tasks</p>
                        <p className="text-sm font-semibold text-[#1A2421]">{outlet.total_done}/{outlet.total_assigned}</p>
                      </div>
                    </div>
                    <div className="border-t border-[#E2E8DF] pt-3 mt-2 space-y-2">
                      {/* Show submitted departments */}
                      {Object.entries(outlet.department_compliance || {}).map(([dept, info]) => {
                        const color = info.score >= 80 ? '#2D6A4F' : info.score >= 50 ? '#D4A017' : '#C45A41';
                        return (
                          <div key={dept} className="flex items-center justify-between" data-testid={`dept-compliance-${dept}`}>
                            <span className="text-xs text-[#1A2421] font-medium">{dept}</span>
                            <div className="flex items-center gap-3">
                              <div className="w-20 h-1.5 bg-[#E2E8DF] rounded-full overflow-hidden">
                                <div className="h-full rounded-full" style={{ width: `${info.score}%`, backgroundColor: color }} />
                              </div>
                              <span className="text-xs font-semibold w-10 text-right" style={{ color }}>{info.score}%</span>
                              {!info.is_on_time && <Clock className="w-3 h-3 text-[#C45A41]" />}
                            </div>
                          </div>
                        );
                      })}
                      {/* Show pending departments */}
                      {(outlet.pending_departments || []).map(dept => (
                        <div key={dept} className="flex items-center justify-between" data-testid={`dept-pending-${dept}`}>
                          <span className="text-xs text-[#5C6B64]">{dept}</span>
                          <span className="text-[10px] text-[#C45A41] font-medium px-2 py-0.5 bg-[#C45A41]/5 rounded">Pending</span>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-3">
                      <div>
                        <p className="text-xs text-[#5C6B64]">Overall Adherence</p>
                        <ScoreDisplay score={outlet.overall_adherence} />
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-[#5C6B64]">Tasks</p>
                        <p className="text-sm font-semibold text-[#1A2421]">{outlet.total_done}/{outlet.total_assigned}</p>
                      </div>
                      {outlet.submitted_time && (
                        <div className="text-right">
                          <p className="text-xs text-[#5C6B64]">Submitted</p>
                          <p className="text-sm font-medium text-[#1A2421]">{outlet.submitted_time}</p>
                        </div>
                      )}
                    </div>
                    {Object.entries(outlet.department_compliance || {}).length > 0 && (
                      <div className="border-t border-[#E2E8DF] pt-3 mt-2 space-y-2">
                        {Object.entries(outlet.department_compliance).map(([dept, info]) => {
                          const color = info.score >= 80 ? '#2D6A4F' : info.score >= 50 ? '#D4A017' : '#C45A41';
                          return (
                            <div key={dept} className="flex items-center justify-between" data-testid={`dept-compliance-${dept}`}>
                              <span className="text-xs text-[#5C6B64]">{dept}</span>
                              <div className="flex items-center gap-3">
                                <div className="w-20 h-1.5 bg-[#E2E8DF] rounded-full overflow-hidden">
                                  <div className="h-full rounded-full" style={{ width: `${info.score}%`, backgroundColor: color }} />
                                </div>
                                <span className="text-xs font-semibold w-10 text-right" style={{ color }}>{info.score}%</span>
                                {!info.is_on_time && <Clock className="w-3 h-3 text-[#C45A41]" />}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
              {/* Drill-down detail panel */}
              {expandedOutlet === outlet.outlet_id && (
                <div className="border-t border-[#E2E8DF] bg-[#F9F9F8] px-4 py-3" data-testid={`drilldown-${outlet.outlet_id}`}
                  onClick={e => e.stopPropagation()}>
                  {drilldownLoading ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="animate-spin w-5 h-5 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
                    </div>
                  ) : drilldownData?.departments && Object.keys(drilldownData.departments).length > 0 ? (
                    <div className="space-y-3">
                      {Object.entries(drilldownData.departments).map(([dept, info]) => (
                        <div key={dept}>
                          <div className="flex items-center justify-between mb-1.5">
                            <p className="text-xs font-semibold text-[#1A2421] uppercase tracking-wide">{dept}</p>
                            <span className="text-[10px] text-[#5C6B64]">
                              {info.submitted_by && `${info.submitted_by}`}
                              {info.submitted_time && ` · ${info.submitted_time}`}
                            </span>
                          </div>
                          <div className="space-y-1">
                            {info.items.map((item, idx) => {
                              const isNo = item.value?.toLowerCase() === 'no';
                              return (
                                <div key={idx} className={`flex items-start justify-between py-1 px-2 rounded text-xs ${isNo ? 'bg-[#C45A41]/5 border border-[#C45A41]/10' : 'bg-white border border-[#E2E8DF]'}`}
                                  data-testid={`drilldown-item-${dept}-${idx}`}>
                                  <span className={`flex-1 ${isNo ? 'text-[#C45A41] font-medium' : 'text-[#1A2421]'}`}>{item.task_name}</span>
                                  <div className="flex items-center gap-2 ml-2 shrink-0">
                                    {isNo ? (
                                      <XCircle className="w-3.5 h-3.5 text-[#C45A41]" />
                                    ) : (
                                      <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6A4F]" />
                                    )}
                                    <span className={`font-medium ${isNo ? 'text-[#C45A41]' : 'text-[#2D6A4F]'}`}>
                                      {item.value || '—'}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-xs text-[#5C6B64] text-center py-2">No checklist data for this date</p>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      ) : rangeData ? (
        /* ===== RANGE VIEW ===== */
        <>
          {/* Summary stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
            {(() => {
              const outlets = rangeData.outlets || [];
              const totalOnTime = outlets.reduce((s, o) => s + o.on_time_count, 0);
              const totalLate = outlets.reduce((s, o) => s + o.late_count, 0);
              const totalMissed = outlets.reduce((s, o) => s + o.missed_count, 0);
              const avgAdherence = outlets.length > 0
                ? Math.round(outlets.reduce((s, o) => s + o.avg_adherence, 0) / outlets.filter(o => o.submitted_days > 0).length || 0)
                : 0;
              return (
                <>
                  <Card className="border-[#E2E8DF] shadow-sm">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] font-medium">Avg Adherence</p>
                      <ScoreDisplay score={avgAdherence} size="md" />
                    </CardContent>
                  </Card>
                  <Card className="border-[#E2E8DF] shadow-sm">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] font-medium">On Time</p>
                      <p className="text-lg font-bold text-[#2D6A4F]" data-testid="total-on-time">{totalOnTime}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E2E8DF] shadow-sm">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] font-medium">Late</p>
                      <p className="text-lg font-bold text-[#C45A41]" data-testid="total-late">{totalLate}</p>
                    </CardContent>
                  </Card>
                  <Card className="border-[#E2E8DF] shadow-sm">
                    <CardContent className="p-4 text-center">
                      <p className="text-xs uppercase tracking-[0.05em] text-[#5C6B64] font-medium">Missed</p>
                      <p className="text-lg font-bold text-[#D4A017]" data-testid="total-missed">{totalMissed}</p>
                    </CardContent>
                  </Card>
                </>
              );
            })()}
          </div>

          {/* Per-outlet range cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {rangeData.outlets?.map(outlet => (
              <Card key={outlet.outlet_id} className="border-[#E2E8DF] shadow-sm" data-testid={`range-card-${outlet.outlet_id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base text-[#1A2421]">{outlet.outlet_name}</CardTitle>
                    <Badge variant="outline" className="border-[#E2E8DF] text-[#5C6B64]" data-testid={`range-days-${outlet.outlet_id}`}>
                      {outlet.submitted_days}/{outlet.days_in_range} days
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {outlet.status === 'no_items' ? (
                    <p className="text-sm text-[#5C6B64]">No checklist items assigned</p>
                  ) : (
                    <>
                      {/* Top metrics row */}
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <p className="text-xs text-[#5C6B64]">Avg Adherence</p>
                          <ScoreDisplay score={outlet.avg_adherence} />
                        </div>
                        <div className="text-right">
                          <TrendBadge trend={outlet.trend} prevAvg={outlet.prev_avg} />
                          {outlet.prev_avg > 0 && (
                            <p className="text-[10px] text-[#5C6B64] mt-0.5">Prev: {outlet.prev_avg}%</p>
                          )}
                        </div>
                      </div>

                      {/* Submission stats */}
                      <div className="grid grid-cols-3 gap-2 mb-3 p-2 bg-[#F9F9F8] rounded-md">
                        <div className="text-center">
                          <CheckCircle2 className="w-3.5 h-3.5 text-[#2D6A4F] mx-auto mb-0.5" />
                          <p className="text-xs font-semibold text-[#2D6A4F]">{outlet.on_time_count}</p>
                          <p className="text-[10px] text-[#5C6B64]">On Time</p>
                        </div>
                        <div className="text-center">
                          <Clock className="w-3.5 h-3.5 text-[#C45A41] mx-auto mb-0.5" />
                          <p className="text-xs font-semibold text-[#C45A41]">{outlet.late_count}</p>
                          <p className="text-[10px] text-[#5C6B64]">Late</p>
                        </div>
                        <div className="text-center">
                          <XCircle className="w-3.5 h-3.5 text-[#D4A017] mx-auto mb-0.5" />
                          <p className="text-xs font-semibold text-[#D4A017]">{outlet.missed_count}</p>
                          <p className="text-[10px] text-[#5C6B64]">Missed</p>
                        </div>
                      </div>

                      {/* Mini trend chart */}
                      {outlet.daily_data?.length > 1 && (
                        <div className="mb-3">
                          <MiniBar data={outlet.daily_data} />
                        </div>
                      )}

                      {/* Department breakdown */}
                      {Object.keys(outlet.department_avg || {}).length > 0 && (
                        <div className="border-t border-[#E2E8DF] pt-3 space-y-2">
                          {Object.entries(outlet.department_avg).map(([dept, score]) => {
                            const color = score >= 80 ? '#2D6A4F' : score >= 50 ? '#D4A017' : '#C45A41';
                            return (
                              <div key={dept} className="flex items-center justify-between" data-testid={`range-dept-${dept}`}>
                                <span className="text-xs text-[#5C6B64]">{dept}</span>
                                <div className="flex items-center gap-3">
                                  <div className="w-20 h-1.5 bg-[#E2E8DF] rounded-full overflow-hidden">
                                    <div className="h-full rounded-full" style={{ width: `${score}%`, backgroundColor: color }} />
                                  </div>
                                  <span className="text-xs font-semibold w-10 text-right" style={{ color }}>{score}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className="text-center text-[#5C6B64] py-10">No data available</p>
      )}

      {/* Issues Section */}
      {!loading && issues.length > 0 && (
        <div className="mt-6" data-testid="issues-section">
          <h3 className="text-lg font-semibold text-[#C45A41] mb-3 flex items-center gap-2">
            <XCircle className="w-5 h-5" /> Checklist Issues ({issues.length})
          </h3>
          {(() => {
            const byOutlet = {};
            for (const issue of issues) {
              const key = issue.outlet_name;
              if (!byOutlet[key]) byOutlet[key] = {};
              const dept = issue.department;
              if (!byOutlet[key][dept]) byOutlet[key][dept] = [];
              byOutlet[key][dept].push(issue);
            }
            return Object.entries(byOutlet).map(([outlet, depts]) => (
              <Card key={outlet} className="border-[#C45A41]/20 shadow-sm mb-3 overflow-hidden" data-testid={`issue-outlet-${outlet}`}>
                <CardHeader className="py-2 px-4 bg-[#C45A41]/5 border-b border-[#C45A41]/10">
                  <CardTitle className="text-sm font-semibold text-[#C45A41]">{outlet}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {Object.entries(depts).map(([dept, deptIssues]) => (
                    <div key={dept}>
                      <div className="px-4 py-1.5 bg-[#F9F9F8] border-b border-[#E2E8DF]">
                        <span className="text-xs font-medium text-[#5C6B64] uppercase tracking-wide">{dept}</span>
                      </div>
                      {deptIssues.map((issue, j) => (
                        <div key={`${issue.task_name}-${issue.submitted_by}-${j}`} className={`flex items-start justify-between px-4 py-2 ${j > 0 ? 'border-t border-[#E2E8DF]' : ''} bg-[#C45A41]/[0.02]`}>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-[#C45A41] font-medium">{issue.task_name}</p>
                            {issue.remarks && <p className="text-xs text-[#5C6B64] mt-0.5 italic">{issue.remarks}</p>}
                          </div>
                          <div className="text-right flex-shrink-0 ml-3">
                            <p className="text-[10px] text-[#5C6B64]">{issue.submitted_by_name || issue.submitted_by}</p>
                            <p className="text-[10px] text-[#5C6B64]">{issue.submitted_time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            ));
          })()}
        </div>
      )}
    </div>
  );
}
